/**
 * Resource Configuration
 * Centralized configuration for all resource types in the Partner Admin
 */
import { BookOpen, Presentation, Monitor, FileCheck, FolderOpen } from 'lucide-react';

// Default Shariyah product folders
export const DEFAULT_SHARIAH_PRODUCTS = [
  'Waqalah',
  'Musharakah',
  'Mudarabah',
  'Ijara',
  'Murabaha',
  'Salam',
  'Istisna',
  'Sukuk',
  'Other'
];

export const RESOURCE_TYPES = {
  ISLAMIC_FINANCE: 'islamic-finance-101',
  SALES_TRAINING: 'sales-training',
  SYSTEM_TRAINING: 'system-training',
  SHARIA_TEMPLATES: 'sharia-templates',
  OTHER_RESOURCES: 'other-resources',
  RESOURCE_LIBRARY: 'resource-library',
};

/** Default accept string for partner resource uploads */
export const DEFAULT_RESOURCE_ACCEPT =
  '.pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png,.ppt,.pptx,.json';

export const RESOURCE_LIBRARY_ACCEPT = '.ppt,.pptx,.json,.pdf';

export const RESOURCE_CONFIG = {
  [RESOURCE_TYPES.ISLAMIC_FINANCE]: {
    label: 'Islamic Financing 101',
    icon: BookOpen,
    iconBgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    emptyStateTitle: 'No Islamic Financing Resources',
    emptyStateMessage: 'Upload Islamic financing 101 materials and resources for partners.',
    uploadButtonLabel: 'Upload Resource',
    modalTitle: 'Upload Islamic Financing Resource',
    s3Prefix: `resources/${RESOURCE_TYPES.ISLAMIC_FINANCE}/`,
    acceptedFileTypes: DEFAULT_RESOURCE_ACCEPT,
  },
  [RESOURCE_TYPES.SALES_TRAINING]: {
    label: 'Sales Training',
    icon: Presentation,
    iconBgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    emptyStateTitle: 'No Sales Training Resources',
    emptyStateMessage: 'Upload sales training materials and resources for partners.',
    uploadButtonLabel: 'Upload Resource',
    modalTitle: 'Upload Sales Training Resource',
    s3Prefix: `resources/${RESOURCE_TYPES.SALES_TRAINING}/`,
    acceptedFileTypes: DEFAULT_RESOURCE_ACCEPT,
  },
  [RESOURCE_TYPES.SYSTEM_TRAINING]: {
    label: 'System Training',
    icon: Monitor,
    iconBgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    emptyStateTitle: 'No System Training Resources',
    emptyStateMessage: 'Upload system training materials and resources for partners.',
    uploadButtonLabel: 'Upload Resource',
    modalTitle: 'Upload System Training Resource',
    s3Prefix: `resources/${RESOURCE_TYPES.SYSTEM_TRAINING}/`,
    acceptedFileTypes: DEFAULT_RESOURCE_ACCEPT,
  },
  [RESOURCE_TYPES.SHARIA_TEMPLATES]: {
    label: 'Shariyah Templates',
    icon: FileCheck,
    iconBgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    emptyStateTitle: 'No Sharia Templates',
    emptyStateMessage: 'Upload Sharia compliance templates for partners.',
    uploadButtonLabel: 'Upload Template',
    modalTitle: 'Upload Sharia Template',
    s3Prefix: `resources/${RESOURCE_TYPES.SHARIA_TEMPLATES}/`,
    acceptedFileTypes: DEFAULT_RESOURCE_ACCEPT,
  },
  [RESOURCE_TYPES.OTHER_RESOURCES]: {
    label: 'Other Resources',
    icon: FolderOpen,
    iconBgColor: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    emptyStateTitle: 'No Other Resources',
    emptyStateMessage: 'Upload additional resources and documentation for partners.',
    uploadButtonLabel: 'Upload Resource',
    modalTitle: 'Upload Resource',
    s3Prefix: `resources/${RESOURCE_TYPES.OTHER_RESOURCES}/`,
    acceptedFileTypes: DEFAULT_RESOURCE_ACCEPT,
  },
  [RESOURCE_TYPES.RESOURCE_LIBRARY]: {
    label: 'Resource Library (Public)',
    icon: BookOpen,
    iconBgColor: 'bg-[#1a4a1a]/10',
    iconColor: 'text-[#1a4a1a]',
    emptyStateTitle: 'No public library packages',
    emptyStateMessage:
      'Upload PowerPoint decks (.pptx) and lesson drafts (content.json) for Professional Development.',
    uploadButtonLabel: 'Upload package file',
    modalTitle: 'Upload Resource Library file',
    s3Prefix: `resources/${RESOURCE_TYPES.RESOURCE_LIBRARY}/`,
    acceptedFileTypes: RESOURCE_LIBRARY_ACCEPT,
  },
};

/**
 * Get resource configuration by type
 */
export const getResourceConfig = (resourceType) => {
  return RESOURCE_CONFIG[resourceType] || RESOURCE_CONFIG[RESOURCE_TYPES.OTHER_RESOURCES];
};

/**
 * Get all resource types as array
 */
export const getAllResourceTypes = () => {
  return Object.values(RESOURCE_TYPES);
};
